import { FiBook } from 'react-icons/fi';

export const awlSublist3Data = {
  courseId: 'awl-sublist-3',
  courseType: 'vocabulary',
  title: 'Academic Word List: Sublist 3',
  icon: FiBook,
  overview: {
    impact: 'Build a deeper academic vocabulary with essential Sublist 3 words',
    focus: '12 key words from Coxhead\'s AWL Sublist 3',
    uses: 'Reading, Writing Task 2',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Word Meanings and Usage',
      content: '<p>Sublist 3 introduces words commonly found in discussions of processes, organisations, and social issues. These words help you express nuanced ideas in IELTS Writing and understand academic Reading passages.</p>',
      points: [
        'Sublist 3 words frequently appear in IELTS Reading academic texts',
        'Many of these words relate to processes, decisions, and social structures',
        'Using them correctly demonstrates a wide vocabulary range for Band 7+',
      ],
      table: {
        headers: ['Word', 'Definition', 'Example Sentence'],
        rows: [
          ['alternative', 'One of two or more available options', 'Renewable energy is a viable alternative to fossil fuels.'],
          ['circumstance', 'A condition or fact that affects a situation', 'Under no circumstances should students plagiarise their work.'],
          ['comment', 'A remark expressing an opinion or reaction', 'The professor refused to comment on the preliminary findings.'],
          ['compensate', 'To make up for something; to pay for loss or damage', 'Higher salaries may compensate for difficult working conditions.'],
          ['component', 'A part or element of a larger whole', 'Effective communication is a key component of leadership.'],
          ['consent', 'Permission or agreement for something to happen', 'Participants must give informed consent before a study begins.'],
          ['considerable', 'Notably large in size, amount, or extent', 'The project required a considerable amount of funding.'],
          ['constant', 'Occurring continuously; unchanging', 'The constant rise in temperatures is a cause for concern.'],
          ['constrain', 'To restrict or limit', 'Budget cuts constrain the ability of schools to hire new staff.'],
          ['contribute', 'To give or add something in order to achieve a result', 'Several factors contribute to the decline in biodiversity.'],
          ['convene', 'To come together for a meeting or assembly', 'The committee will convene next Monday to discuss the proposal.'],
          ['coordinate', 'To organise activities so they work together effectively', 'The agencies must coordinate their efforts to respond to the crisis.'],
        ],
      },
    },
    {
      title: 'Word Families',
      content: '<p>Many Sublist 3 words have rich word families. Knowing all forms helps you avoid repetition and demonstrate grammatical accuracy in IELTS.</p>',
      points: [
        'Word family knowledge is essential for paraphrasing in Reading and Writing',
        'Using the wrong word form is a frequent error in Band 5-6 essays',
      ],
      table: {
        headers: ['Base Word', 'Noun', 'Verb', 'Adjective/Adverb'],
        rows: [
          ['alternative', 'alternative', '—', 'alternative, alternatively'],
          ['circumstance', 'circumstance', '—', 'circumstantial'],
          ['comment', 'comment, commentary, commentator', 'comment', '—'],
          ['compensate', 'compensation', 'compensate', 'compensatory'],
          ['component', 'component', '—', '—'],
          ['consent', 'consent', 'consent', '—'],
          ['considerable', '—', '—', 'considerable, considerably'],
          ['constant', 'constancy', '—', 'constant, constantly'],
          ['constrain', 'constraint', 'constrain', 'constrained'],
          ['contribute', 'contribution, contributor', 'contribute', 'contributory'],
          ['convene', 'convention, convenor', 'convene', 'conventional, conventionally'],
          ['coordinate', 'coordination, coordinator', 'coordinate', 'coordinated'],
        ],
      },
    },
    {
      title: 'Common Collocations',
      content: '<p>Natural collocations help your writing sound polished and academic. These are the most useful pairings for IELTS.</p>',
      points: [
        'Strong collocations are often tested in IELTS Reading gap-fill questions',
        'Using them shows examiners you have an advanced vocabulary range',
      ],
      examples: [
        { label: 'alternative', text: 'a viable alternative, alternative approach, alternative energy, no alternative but to' },
        { label: 'circumstance', text: 'under the circumstances, exceptional circumstances, in certain circumstances' },
        { label: 'compensate', text: 'compensate for, adequate compensation, financial compensation' },
        { label: 'component', text: 'key component, essential component, major component, component parts' },
        { label: 'consent', text: 'informed consent, give consent, parental consent, without consent' },
        { label: 'considerable', text: 'considerable amount, considerable effort, considerable impact, considerable attention' },
        { label: 'constant', text: 'constant change, constant pressure, remain constant, at a constant rate' },
        { label: 'constrain', text: 'severely constrained, budget constraints, time constraints, constrain growth' },
        { label: 'contribute', text: 'contribute to, make a contribution, significant contribution, contributing factor' },
        { label: 'coordinate', text: 'coordinate efforts, coordinate activities, coordination between, well-coordinated' },
      ],
    },
    {
      title: 'Using AWL Words in IELTS Writing',
      content: '<p>Sublist 3 words are ideal for discussing causes, solutions, and processes — common Task 2 themes.</p>',
      points: [
        'Use "contribute to" when discussing causes ("Several factors contribute to...")',
        'Replace "a lot of" with "considerable" for a more academic tone',
        'Use "constraint" and "alternative" when discussing problems and solutions',
      ],
      examples: [
        { label: 'Weak sentence', text: 'A lot of money was spent on the project and it was hard to organise everything.' },
        { label: 'Strong sentence', text: 'A considerable amount of funding was allocated, and the team had to coordinate multiple activities under tight constraints.' },
        { label: 'Weak sentence', text: 'Many things lead to environmental problems.' },
        { label: 'Strong sentence', text: 'Several contributing factors, including industrial emissions and deforestation, have led to considerable environmental damage.' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Choose the correct definition for each academic word.',
      questions: [
        { text: 'What does "compensate" mean?', options: ['To make up for something or pay for a loss', 'To compete with someone', 'To complain about a situation'], answer: 'To make up for something or pay for a loss', explanation: '"Compensate" means to provide something good to balance or offset something bad.' },
        { text: 'What does "constrain" mean?', options: ['To encourage freely', 'To restrict or limit', 'To construct or build'], answer: 'To restrict or limit', explanation: '"Constrain" means to impose limits. Don\'t confuse it with "construct" (to build).' },
        { text: 'What does "considerable" mean?', options: ['Thoughtful and kind', 'Notably large in size or extent', 'Insignificant or minor'], answer: 'Notably large in size or extent', explanation: '"Considerable" means large or significant. Don\'t confuse with "considerate" (thoughtful).' },
        { text: 'What does "consent" mean?', options: ['Permission or agreement', 'An argument or disagreement', 'A legal document'], answer: 'Permission or agreement', explanation: '"Consent" means voluntary agreement, often used in research and legal contexts.' },
        { text: 'What does "convene" mean?', options: ['To be convenient', 'To come together for a meeting', 'To convince someone'], answer: 'To come together for a meeting', explanation: '"Convene" means to assemble or call together a group, especially for a formal meeting.' },
      ],
    },
    {
      title: 'Complete the Sentence',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct word from Sublist 3.',
      questions: [
        { text: 'Several factors ___ to the rising cost of living in urban areas.', answer: 'contribute', explanation: '"Contribute to" is a key collocation meaning to be one of the causes of something.' },
        { text: 'Public transport is a practical ___ to driving in congested cities.', answer: 'alternative', explanation: '"Alternative to" indicates a different option or choice.' },
        { text: 'The research required a ___ amount of time and resources.', answer: 'considerable', explanation: '"A considerable amount" is a formal way of saying "a lot" in academic writing.' },
        { text: 'All participants gave their ___ before the experiment began.', answer: 'consent', explanation: '"Give consent" is the standard collocation, especially in research ethics.' },
      ],
    },
    {
      title: 'Choose the Correct Word Form',
      type: 'select',
      instructions: 'Select the grammatically correct form of the word for each sentence.',
      questions: [
        { text: 'Her ___ to the project was invaluable. (contribute)', options: ['contribution', 'contributor', 'contribute'], answer: 'contribution', explanation: '"Her contribution" requires a noun — "contribution" is the correct form.' },
        { text: 'The workers received ___ for the overtime hours. (compensate)', options: ['compensate', 'compensation', 'compensatory'], answer: 'compensation', explanation: '"Received compensation" requires a noun after the verb.' },
        { text: 'Budget ___ forced the school to reduce programmes. (constrain)', options: ['constraints', 'constrain', 'constrained'], answer: 'constraints', explanation: '"Budget constraints" is a common noun collocation.' },
        { text: 'The two departments need better ___ to avoid duplication. (coordinate)', options: ['coordinate', 'coordination', 'coordinated'], answer: 'coordination', explanation: '"Better coordination" requires the noun form after the adjective.' },
      ],
    },
    {
      title: 'Use Words in Sentences',
      type: 'writing',
      instructions: 'Write a sentence using the given word appropriately in an academic context.',
      questions: [
        { text: 'Write a sentence using "contribute" about causes of pollution.', example: 'Industrial emissions and vehicle exhaust contribute significantly to air pollution in major cities.', tip: 'Use "contribute to" followed by the result or effect — it\'s a common IELTS cause-effect pattern.' },
        { text: 'Write a sentence using "considerable" to describe a challenge.', example: 'Implementing the new policy required considerable effort from both the government and local authorities.', tip: 'Pair with "amount", "effort", "impact", or "attention" for a strong collocation.' },
        { text: 'Write a sentence using "alternative" in the context of energy.', example: 'Investing in alternative energy sources such as solar and wind power is essential for sustainable development.', tip: 'Use "alternative to" or "alternative + noun" (e.g. alternative energy, alternative approach).' },
      ],
    },
  ],
  tips: [
    'Don\'t confuse "considerable" (large) with "considerate" (thoughtful toward others)',
    '"Contribute to" is one of the most useful phrases for cause-effect essays — practise it often',
    'Learn "constraint" and "constrain" together — the noun form is more common in IELTS Writing',
    'Use "coordinate" and "component" when discussing how parts of a system work together',
  ],
  dailyPractice: [
    'Write a short cause-and-effect paragraph using "contribute", "considerable", and "component"',
    'Find a news article about policy or government and identify Sublist 3 words',
    'Practise sentence transformation: rewrite informal sentences using Sublist 3 vocabulary',
    'Create flashcards with the word on one side and a collocation + example on the other',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'AWL Sublist 3 vocabulary course covering 12 key academic words: alternative, circumstance, comment, compensate, component, consent, considerable, constant, constrain, contribute, convene, coordinate. Includes word meanings, word families, collocations, and IELTS Writing integration.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...awlSublist3Data,
      title: awlSublist3Data.title + ' (Foundations)',
      overview: { ...awlSublist3Data.overview, duration: '15-20 minutes' },
      sections: awlSublist3Data.sections.slice(0, Math.max(2, Math.ceil(awlSublist3Data.sections.length / 2))),
      exercises: awlSublist3Data.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...awlSublist3Data,
      title: awlSublist3Data.title + ' (Advanced)',
      overview: { ...awlSublist3Data.overview, duration: '30-35 minutes' },
      sections: awlSublist3Data.sections,
      exercises: awlSublist3Data.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(awlSublist3Data.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return awlSublist3Data;
}
